// TelcoTrack - Authentication Service
// Real auth service connecting to backend API

import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { tap, catchError, map, timeout, finalize, shareReplay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { LoggerService } from './logger.service';
import { ErrorHandlingService } from './error-handling.service';
import { resolveUserStavbyVisibility, userCanSeePolygon } from '../shared-bridge';

export type TokenRefreshOutcome = 'refreshed' | 'unauthorized' | 'transient_error';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly apiUrl = `${environment.apiUrl}${environment.authServiceUrl}`;
  private readonly isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  private readonly currentUserSubject = new BehaviorSubject<any>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly logger: LoggerService,
    private readonly errorHandler: ErrorHandlingService
  ) {
    // localStorage is a UI cache only — authorization must come from /api/auth/me (cookie session).
    const isLoggedIn = localStorage.getItem('cubby_logged_in') === 'true';
    const cached = this.readCachedUser();
    this.isAuthenticatedSubject.next(isLoggedIn);
    this.currentUserSubject.next(cached);
    this.installBackgroundSessionKeepAlive();
  }

  /**
   * Check if user is logged in
   */
  isLoggedIn(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  /**
   * Login user
   * Note: withCredentials: true is required for HTTP-only cookie authentication
   */
  login(email: string, password: string): Observable<boolean> {
    return this.http.post<any>(`${this.apiUrl}/login`, {
      email,
      password
    }, { withCredentials: true }).pipe(
      map(response => {
        if (response.success) {
          // Store user data in localStorage for frontend state management
          // Authentication is handled by HTTP-only cookies
          localStorage.setItem('cubby_logged_in', 'true');
          localStorage.setItem('cubby_user', JSON.stringify(response.data.user));
          
          // Update state
          this.lastProactiveRefreshAt = Date.now();
          this.isAuthenticatedSubject.next(true);
          this.currentUserSubject.next(response.data.user);
          
          return true;
        }
        throw new Error(response.message || 'Login failed');
      }),
      catchError(error => {
        const message = this.errorHandler.getErrorMessage(error) || 'Login failed. Please check your credentials.';
        this.logger.error('Login error:', error);
        return throwError(() => new Error(message));
      })
    );
  }

  /**
   * Logout user
   * Note: withCredentials: true is required to send HTTP-only cookies for logout
   */
  logout(): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/logout`, {}, { withCredentials: true }).pipe(
      tap(() => {
        // Clear local storage
        localStorage.removeItem('cubby_logged_in');
        localStorage.removeItem('cubby_user');
        
        // Update state
        this.clearSessionState();
      }),
      catchError(error => {
        this.logger.error('Logout error:', error);
        // Even if logout fails on server, clear local state
        localStorage.removeItem('cubby_logged_in');
        localStorage.removeItem('cubby_user');
        this.clearSessionState();
        return throwError(() => error);
      })
    );
  }

  /**
   * Request password reset. Sends email with reset link if user exists.
   */
  forgotPassword(email: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/forgot-password`, { email }).pipe(
      map(response => {
        if (response.success) return response;
        throw new Error(response.message || 'Request failed');
      }),
      catchError(error => {
        const message = this.errorHandler.getErrorMessage(error) || 'Nepodařilo se odeslat odkaz. Zkuste to prosím znovu.';
        this.logger.error('Forgot password error:', error);
        return throwError(() => new Error(message));
      })
    );
  }

  /**
   * Reset password using token from email link
   */
  resetPassword(token: string, newPassword: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/reset-password`, { token, newPassword }).pipe(
      map(response => {
        if (response.success) return response;
        throw new Error(response.message || 'Reset failed');
      }),
      catchError(error => {
        const message = this.errorHandler.getErrorMessage(error) || 'Reset hesla se nezdařil. Zkuste to prosím znovu.';
        this.logger.error('Reset password error:', error);
        return throwError(() => new Error(message));
      })
    );
  }

  /**
   * Register new user
   * Note: withCredentials: true is required to receive HTTP-only cookies after registration
   */
  register(userData: { email: string; password: string; firstName?: string; lastName?: string }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/register`, userData, { withCredentials: true }).pipe(
      map(response => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Registration failed');
      }),
      catchError(error => {
        const message = this.errorHandler.getErrorMessage(error) || 'Registration failed. Please try again.';
        this.logger.error('Registration error:', error);
        return throwError(() => new Error(message));
      })
    );
  }

  canSeePolygon(polygonId?: string | null): boolean {
    return userCanSeePolygon(resolveUserStavbyVisibility(this.currentUserSubject.value ?? this.getCurrentUser()), polygonId);
  }

  /**
   * In-memory session user (refreshed from `/api/auth/me`). Prefer this for authz UI.
   * localStorage `cubby_user` is a cache for initial paint only — never trust it alone.
   */
  getCurrentUser(): any {
    return this.currentUserSubject.value ?? this.readCachedUser();
  }

  /** Roles from the in-memory session (server-backed after validateAuthentication / login). */
  getSessionRoles(): string[] {
    const roles = this.getCurrentUser()?.roles;
    return Array.isArray(roles) ? roles.map((r: string) => String(r || '').toLowerCase()) : [];
  }

  private readCachedUser(): any {
    const userStr = localStorage.getItem('cubby_user');
    if (!userStr) return null;

    try {
      return JSON.parse(userStr);
    } catch (error) {
      this.logger.error('Error parsing user data from localStorage:', error);
      localStorage.removeItem('cubby_user');
      return null;
    }
  }

  /**
   * Check if authentication is valid by calling backend `/me` (records app activity + slides session).
   * Note: withCredentials: true is required to send HTTP-only cookies for authentication validation
   */
  /** Timeout for /me request so the app does not hang on white screen if backend is unreachable */
  private readonly authValidationTimeoutMs = 15_000;
  /** Avoid refresh spam: authGuard calls validateAuthentication on every navigation. */
  private lastProactiveRefreshAt = 0;
  private readonly proactiveRefreshMinIntervalMs = 30 * 60 * 1000;
  private readonly resumeRefreshMinIntervalMs = 15 * 60 * 1000;
  /** Single in-flight refresh observable shared across concurrent 401 callers. */
  private refreshOutcomeInFlight$: Observable<TokenRefreshOutcome> | null = null;
  /**
   * authGuard hits /me on every canActivate. Cache successes briefly so a redirect
   * storm (or rapid in-app navigations) cannot fire dozens of /me per second.
   */
  private readonly authValidationCacheMs = 60_000;
  private lastAuthValidationAt = 0;
  private lastAuthValidationOk = false;
  private authValidationInFlight$: Observable<boolean> | null = null;

  private installBackgroundSessionKeepAlive(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    window.setInterval(() => {
      if (this.isLoggedIn()) {
        this.maybeProactiveRefresh(this.proactiveRefreshMinIntervalMs);
      }
    }, this.proactiveRefreshMinIntervalMs);

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.isLoggedIn()) {
        this.maybeProactiveRefresh(this.resumeRefreshMinIntervalMs);
      }
    });

    window.addEventListener('online', () => {
      if (this.isLoggedIn()) {
        this.maybeProactiveRefresh(this.resumeRefreshMinIntervalMs);
      }
    });
  }

  private maybeProactiveRefresh(minIntervalMs: number): void {
    const now = Date.now();
    if (now - this.lastProactiveRefreshAt < minIntervalMs) {
      return;
    }
    this.lastProactiveRefreshAt = now;
    this.refreshToken().subscribe();
  }

  validateAuthentication(): Observable<boolean> {
    const now = Date.now();
    if (
      this.lastAuthValidationOk &&
      now - this.lastAuthValidationAt < this.authValidationCacheMs &&
      this.isLoggedIn()
    ) {
      return of(true);
    }
    if (this.authValidationInFlight$) {
      return this.authValidationInFlight$;
    }

    this.authValidationInFlight$ = this.http.get<any>(`${this.apiUrl}/me`, { withCredentials: true }).pipe(
      timeout(this.authValidationTimeoutMs),
      map(response => {
        const user = response?.data?.user ?? response?.user;
        if (response?.success && user) {
          localStorage.setItem('cubby_user', JSON.stringify(user));
          localStorage.setItem('cubby_logged_in', 'true');
          this.currentUserSubject.next(user);
          if (!this.isAuthenticatedSubject.value) {
            this.isAuthenticatedSubject.next(true);
          }
          this.lastAuthValidationOk = true;
          this.lastAuthValidationAt = Date.now();
          this.maybeProactiveRefresh(this.proactiveRefreshMinIntervalMs);
          return true;
        }
        this.lastAuthValidationOk = false;
        this.logger.warn('Authentication validation returned false, but not logging out');
        return false;
      }),
      catchError(error => {
        this.lastAuthValidationOk = false;
        this.logger.error('Authentication validation failed:', error);
        // Don't automatically logout on error - let the guard decide
        // This prevents clearing auth state when network errors occur
        return throwError(() => new Error('Authentication validation failed'));
      }),
      finalize(() => {
        this.authValidationInFlight$ = null;
      }),
      shareReplay(1),
    );
    return this.authValidationInFlight$;
  }

  /**
   * Refresh access token using refresh token cookie (deduplicated across concurrent callers).
   * Distinguishes definitive auth rejection (401/403) from transient network/5xx errors.
   */
  refreshTokenWithOutcome(): Observable<TokenRefreshOutcome> {
    if (this.refreshOutcomeInFlight$) {
      return this.refreshOutcomeInFlight$;
    }

    this.refreshOutcomeInFlight$ = this.http
      .post<any>(`${this.apiUrl}/refresh`, {}, { withCredentials: true })
      .pipe(
        map((response): TokenRefreshOutcome => {
          if (response?.success) {
            this.lastProactiveRefreshAt = Date.now();
            return 'refreshed';
          }
          return 'unauthorized';
        }),
        catchError((error: HttpErrorResponse | unknown): Observable<TokenRefreshOutcome> => {
          const status = (error as HttpErrorResponse | undefined)?.status ?? 0;
          if (status === 401 || status === 403) {
            return of('unauthorized');
          }
          this.logger.warn('Transient error during token refresh; keeping session state:', error);
          return of('transient_error');
        }),
        finalize(() => {
          this.refreshOutcomeInFlight$ = null;
        }),
        shareReplay(1),
      );

    return this.refreshOutcomeInFlight$;
  }

  /**
   * Refresh access token using refresh token cookie.
   * Returns true if refresh succeeded (new cookies set), false otherwise.
   */
  refreshToken(): Observable<boolean> {
    return this.refreshTokenWithOutcome().pipe(
      map((outcome) => outcome === 'refreshed'),
    );
  }

  /**
   * Force logout without calling backend
   */
  forceLogout(): void {
    localStorage.removeItem('cubby_logged_in');
    localStorage.removeItem('cubby_user');
    this.clearSessionState();
  }

  private clearSessionState(): void {
    this.lastAuthValidationOk = false;
    this.lastAuthValidationAt = 0;
    this.authValidationInFlight$ = null;
    this.isAuthenticatedSubject.next(false);
    this.currentUserSubject.next(null);
  }

}